/**
 * Decide what the audience must pick before a show can play, from a script's
 * `zoneConfig`.
 *
 * Two independent choices can be required:
 *   - a ZONE / sector — either stadium sectors (`sectorEnabled`) or a plain
 *     named option list (`options`, inputType 'select', e.g. "Spanien"/"Italien")
 *   - a ROW number (`rowEnabled`)
 *
 * The bug this guards against (MM "Flaggen A und B"): a plain option list has
 * `sectorEnabled: false` but still needs the user to choose. Keying the sector
 * choice ONLY off `sectorEnabled` made the client skip the picker entirely and
 * auto-assign zone 0, so every phone played the first flag and no selection UI
 * ever showed — while the data (`enabled`, `options`) was perfectly correct.
 *
 * @param {object|null|undefined} zc  the script's zoneConfig
 * @returns {{ needsSelection: boolean, hasSector: boolean, hasRow: boolean }}
 */
/**
 * Zone options may be a plain string ("Nord") OR a structured label with a main
 * caption + a description, e.g. { caption: "Unterrang", description: "Reihe 1–20:
 * Sektoren A–T" } (#323). Normalise either shape to { value, caption, description }
 * so the ZoneSelector and the studio treat them uniformly.
 *
 * `value` is the stable identity (used for name matching in the studio). Audience
 * selection is by INDEX, so adding a description never changes which zone a phone
 * joins. Back-compatible: a bare string keeps working untouched.
 *
 * @param {string|{value?:string,caption?:string,label?:string,description?:string}|null|undefined} option
 * @returns {{ value:string, caption:string, description:string }}
 */
export function normalizeZoneOption(option) {
  if (option == null) return { value: '', caption: '', description: '' };
  if (typeof option === 'string') {
    // A flat string may carry a description via the same "Caption | Description"
    // convention the studio uses (parseZoneList). Split here too so the client
    // button shows the description whether options arrive as objects OR strings.
    const bar = option.indexOf('|');
    if (bar !== -1) {
      const caption = option.slice(0, bar).trim();
      const description = option.slice(bar + 1).trim();
      if (caption) return { value: caption, caption, description };
    }
    return { value: option, caption: option, description: '' };
  }
  const caption = option.caption ?? option.value ?? option.label ?? '';
  const value = option.value ?? option.caption ?? option.label ?? '';
  return { value: String(value), caption: String(caption), description: String(option.description ?? '') };
}

export function zoneSelectionNeeded(zc) {
  if (!zc?.enabled) return { needsSelection: false, hasSector: false, hasRow: false };
  // A sector/zone choice is needed when there are named options to pick from,
  // OR the legacy sector flag is set. The `?? (!rowEnabled && enabled)` fallback
  // keeps old configs working where `sectorEnabled` was never written.
  const hasSector =
    (zc.options?.length ?? 0) > 0 || (zc.sectorEnabled ?? (!zc.rowEnabled && zc.enabled));
  const hasRow = zc.rowEnabled || false;
  return { needsSelection: hasSector || hasRow, hasSector, hasRow };
}
